/* "Complete the look" — same-team suggestions in the cart drawer, cart page and product page.
   Markup comes from snippets/cart-upsell-slot.liquid; the suggestions from
   sections/cart-upsell.liquid through the Section Rendering API.

   Tracking, so we can tell whether it sells:
   - Shopify: every added item carries the hidden line property _upsell=<placement>; the cart
     gets the attribute _upsell_used=<placement> (cleared again if no upsold line remains, see
     <cart-upsell-cleanup>). Events also go through Shopify.analytics.publish
     ('pjs_upsell_viewed' / 'pjs_upsell_added').
   - Plausible: "Upsell Viewed" (counted once the block is actually on screen) and
     "Upsell Added", with placement, team and product. */
(function () {
  if (customElements.get('cart-upsell')) return;

  var cache = {};     // url -> html
  var inflight = {};  // url -> Promise<html>, so two blocks on one page share one request
  var viewed = {};    // placement|team -> true, one view per page load

  function escapeHtml(t) {
    return String(t).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; });
  }

  function fetchHtml(url) {
    if (cache[url]) return Promise.resolve(cache[url]);
    if (!inflight[url]) {
      /* Resolves to the section HTML, or '' when the response is not the section. */
      var get = function () {
        return fetch(url, { headers: { Accept: 'text/html' } })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (t) { return t.indexOf('data-cd-up-list') !== -1 ? t : ''; })
          .catch(function () { return ''; });
      };
      /* One retry: Shopify throttles bursts (429), briefly 404s a section while a theme file is
         being published, and can answer a brand-new session with something else. */
      inflight[url] = get()
        .then(function (html) { return html || new Promise(function (ok) { setTimeout(ok, 1200); }).then(get); })
        .then(function (html) { if (html) cache[url] = html; delete inflight[url]; return html; })
        .catch(function () { delete inflight[url]; return ''; });
    }
    return inflight[url];
  }

  /* A size select starts on the first size in stock, which is usually the smallest (S, 6 7/8).
     Start on the most common size instead; the shopper still sees and can change it. */
  var COMMON_SIZES = ['7 3/8', '7 1/4', '7 1/2', 'l', 'm', 'xl', '48', '44', 's/m', 'm/l', 'l/xl'];
  /* "M (10/12)", "m(10/12)", "40 / Deep Royal" -> "m", "m", "40". */
  function sizeKey(text) {
    return String(text || '').split(' / ')[0].toLowerCase().replace(/\(.*$/, '').replace(/\s+/g, ' ').trim();
  }
  function findSize(select, key) {
    return Array.prototype.find.call(select.options, function (o) { return sizeKey(o.text) === key; });
  }
  function commonSize(select) {
    if (!select || select.hasAttribute('data-touched')) return;
    for (var i = 0; i < COMMON_SIZES.length; i++) {
      var hit = findSize(select, COMMON_SIZES[i]);
      if (hit) { select.value = hit.value; return; }
    }
  }
  function optionPrice(select, fallback) {
    var o = select && select.options[select.selectedIndex];
    var p = o && parseFloat(o.getAttribute('data-price'));
    return isNaN(p) || p == null ? fallback : p;
  }
  function money(n) { return '$' + n.toFixed(2); }
  function cartJsUrl() {
    return ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart.js';
  }

  /* Resolves once Plausible has sent the event (or after a short cap), so a page reload
     right after does not cancel it. */
  function track(name, shopifyName, props) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(shopifyName, props);
    } catch (e) {}
    return new Promise(function (resolve) {
      var done = false;
      var finish = function () { if (!done) { done = true; resolve(); } };
      try {
        if (window.plausible) window.plausible(name, { props: props, callback: finish });
        else finish();
      } catch (e) { finish(); }
      setTimeout(finish, 1200);
    });
  }

  /* Attribute writes run one after another, so an older "clear" can never land after a newer
     "set". */
  var attrChain = Promise.resolve();
  function postAttribute(value) {
    return fetch(window.routes.cart_update_url + '.js', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _upsell_used: value } })
    });
  }
  function setUpsellAttribute(value) {
    attrChain = attrChain.then(function () { return postAttribute(value); }).catch(function () {});
    return attrChain;
  }
  function clearUpsellAttributeIfStale() {
    attrChain = attrChain.then(function () {
      return fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var upsold = (cart.items || []).some(function (i) { return i.properties && i.properties._upsell; });
          if (!upsold && cart.attributes && cart.attributes._upsell_used) return postAttribute('');
        });
    }).catch(function () {});
    return attrChain;
  }

  customElements.define('cart-upsell', class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.busy = false;
      this.addEventListener('click', (e) => {
        var button = e.target.closest('[data-cd-up-add]');
        if (button) this.add(button);
      });
      /* Some items cost more in bigger sizes: keep the shown price on the selected size. */
      this.addEventListener('change', (e) => {
        /* Keep our changes to ourselves: on the cart page this block sits inside <cart-items>,
           which treats any change event as a line-quantity edit. */
        e.stopPropagation();
        if (!e.target.matches('select[data-cd-up-variant]')) return;
        e.target.setAttribute('data-touched', '');
        var card = e.target.closest('[data-cd-up-product]');
        var price = optionPrice(e.target, parseFloat(card.dataset.cdUpPrice) || 0);
        card.dataset.cdUpPrice = String(price);
        var p = card.querySelector('.cd-up-p');
        if (p) p.textContent = money(price);
      });
      /* In the drawer, wait until it is actually opened: the drawer lives in the layout, so
         loading right away would cost a request (and a false "view") on every page. */
      var drawer = this.closest('cart-drawer');
      if (drawer && !drawer.classList.contains('is-open')) {
        this.waitForOpen = new MutationObserver(() => {
          if (drawer.classList.contains('is-open')) { this.waitForOpen.disconnect(); this.load(); }
        });
        this.waitForOpen.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      } else if (!drawer && 'IntersectionObserver' in window) {
        /* Product and cart page: fetch when the block comes near the screen. */
        this.near = new IntersectionObserver((entries) => {
          if (entries.some(function (en) { return en.isIntersecting; })) { this.near.disconnect(); this.load(); }
        }, { rootMargin: '600px 0px' });
        this.near.observe(this.hidden ? (this.parentElement || this) : this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this.waitForOpen) this.waitForOpen.disconnect();
      if (this.seen) this.seen.disconnect();
      if (this.near) this.near.disconnect();
    }

    load() {
      var sources = [];
      try { sources = JSON.parse(this.dataset.sources || '[]'); } catch (e) { sources = []; }
      if (!sources.length) return;
      Promise.all(sources.map(function (src) {
        return fetchHtml(src.u).then(function (html) { return { src: src, html: html }; });
      })).then((results) => { if (this.isConnected) this.paint(results); });
    }

    /* Score every candidate from the team's categories and keep the best few.
       Weights follow the v2 plan (docs: "Complete the look v2 — plan"):
         pairing fit 0-30 · audience 0-20 · stock depth 0-20 (+3 high-inventory)
         best-seller rank 0-15 · price vs the anchor -15..+5
       Then at most 2 of one kind, no second hat when the cart already has this team's hat,
       and hide when fewer than `min` survive. Always the same team (the sources are team URLs). */
    paint(results) {
      var exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
      var cartKinds = (this.dataset.cartKinds || '').split(',').filter(Boolean);
      var limit = parseInt(this.dataset.limit, 10) || 3;
      var min = parseInt(this.dataset.min, 10) || 2;
      var aud = this.dataset.audience || 'adult';
      var anchorPrice = parseFloat(this.dataset.anchorPrice) || 0;
      var inCart = !this.closest('[data-cd-product-page]') && this.dataset.placement !== 'product-page';
      var best = {};

      results.forEach(function (res) {
        if (!res.html) return;
        var holder = document.createElement('template');
        holder.innerHTML = res.html;
        holder.content.querySelectorAll('[data-cd-up-product]').forEach(function (card) {
          var id = card.dataset.cdUpProduct;
          if (exclude.indexOf(id) !== -1) return;
          var kind = card.dataset.kind || 'other';
          if (inCart && kind === 'hat' && cartKinds.indexOf('hat') !== -1) return;
          var cAud = card.dataset.aud || 'adult';
          /* Kids', toddler and infant items only on pages of that same size group; women's items
             not on men's pages. Kids' pages may get adult hats, but only adjustable ones. */
          var small = { youth: 1, toddler: 1, infant: 1 };
          if (small[cAud] && cAud !== aud) return;
          if ((aud === 'toddler' || aud === 'infant') && cAud !== aud) return;
          if (cAud === 'womens' && aud === 'adult') return;
          if (aud === 'youth' && kind === 'hat' && card.dataset.fit === '1') return;
          var audScore;
          if (cAud === aud) audScore = 20;
          else if (aud === 'youth' && kind === 'hat') audScore = 14;
          else if (aud === 'womens' && kind === 'hat') audScore = 12;
          else if (aud === 'womens' && cAud === 'adult') audScore = 4;
          else audScore = 10;
          var units = parseInt(card.dataset.units, 10) || 0;
          var depth = Math.min(20, 20 * Math.log10(Math.max(units, 1)) / Math.log10(500)) + (card.dataset.hi === '1' ? 3 : 0);
          var rank = parseInt(card.dataset.rank, 10) || 12;
          var demand = Math.max(0, 15 - (rank - 1) * 1.25);
          var price = parseFloat(card.dataset.cdUpPrice) || 0;
          var priceScore = 0;
          if (anchorPrice > 0) priceScore = price <= anchorPrice ? 5 : (price <= anchorPrice * 1.25 ? 0 : -15);
          var score = (res.src.w || 0) + audScore + depth + demand + priceScore;
          if (!best[id] || best[id].score < score) best[id] = { card: card, kind: kind, score: score };
        });
      });

      var ranked = Object.keys(best).map(function (k) { return best[k]; }).sort(function (a, b) { return b.score - a.score; });
      /* The same item in another color reads as a duplicate ("Navy FUSE Tee" + "Orange FUSE
         Tee"): keep only the best-scored color. */
      var colors = /\b(navy|orange|white|black|gr[ae]y|heather|charcoal|red|scarlet|maroon|royal|light|powder|blue|green|kelly|gold|cream|pink|purple|silver|teal|brown|natural|camo)\b/g;
      var stems = {};
      var picked = [];
      var perKind = {};
      ranked.forEach(function (c) {
        if (picked.length >= limit) return;
        if ((perKind[c.kind] || 0) >= 2) return;
        var stem = (c.card.dataset.cdUpTitle || '').toLowerCase().replace(colors, '').replace(/\s+/g, ' ').trim();
        if (stems[stem]) return;
        stems[stem] = true;
        perKind[c.kind] = (perKind[c.kind] || 0) + 1;
        picked.push(c);
      });
      if (picked.length < min) return;

      var list = document.createElement('div');
      list.className = 'cd-up-list';
      list.setAttribute('data-cd-up-list', '');
      picked.forEach(function (c, i) {
        c.card.dataset.pos = String(i + 1);
        var node = document.importNode(c.card, true);
        var sel = node.querySelector('select[data-cd-up-variant]');
        commonSize(sel);
        if (sel && sel.selectedIndex >= 0) {
          var pr = optionPrice(sel, parseFloat(node.dataset.cdUpPrice) || 0);
          node.dataset.cdUpPrice = String(pr);
          var pe = node.querySelector('.cd-up-p');
          if (pe && pr !== parseFloat(c.card.dataset.cdUpPrice)) pe.textContent = money(pr);
        }
        list.appendChild(node);
      });
      var slot = this.querySelector('[data-cd-up-slot]');
      slot.innerHTML = '';
      /* Bundle: the top pick plus the best pick of a different kind (a hat and a tee beat two tees). */
      var duo = picked.slice(0, 1);
      var other = picked.find(function (c) { return picked[0] && c.kind !== picked[0].kind; });
      duo.push(other || picked[1]);
      duo = duo.filter(Boolean);
      if (this.dataset.layout === 'bundle' && this.renderBundle(slot, duo)) {
        /* bundle drawn */
      } else {
        slot.appendChild(list);
      }
      this.hidden = false;
      var onScreen = this.bundle ? duo : picked;
      this.shown = onScreen.length;
      this.shownKinds = onScreen.map(function (c) { return c.kind; }).join(',');
      /* Read by the QA harness (what was picked, whatever the layout). */
      this.picked = onScreen.map(function (c) { var d = c.card.dataset; return { id: d.cdUpProduct, title: d.cdUpTitle, kind: d.kind, aud: d.aud, units: +d.units, price: +d.cdUpPrice, score: Math.round(c.score) }; });
      this.watchView();
    }

    /* "Add both": the current product plus the top suggestions, with checkboxes, a live total
       and one button. Uses the size chosen in the product form. Returns false (falls back to the
       list) when the page has no standard product form. */
    renderBundle(slot, picked) {
      var form = document.querySelector('product-form form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
      var idInput = form && form.querySelector('[name="id"]');
      if (!idInput || !picked.length) return false;
      var anchorTitle = this.dataset.anchorTitle || 'This item';
      var anchorImg = this.dataset.anchorImage || '';
      var anchorPrice = parseFloat(this.dataset.anchorPrice) || 0;

      var wrap = document.createElement('div');
      wrap.className = 'cd-bd';
      var thumbs = '<div class="cd-bd-thumbs">' +
        '<span class="cd-bd-th">' + (anchorImg ? '<img src="' + anchorImg + '" alt="" width="64" height="64" loading="lazy">' : '') + '</span>';
      picked.forEach(function (c) {
        var img = c.card.querySelector('.cd-up-ph img');
        thumbs += '<span class="cd-bd-plus" aria-hidden="true">+</span><span class="cd-bd-th">' +
          (img ? '<img src="' + img.getAttribute('src') + '" alt="" width="64" height="64" loading="lazy">' : '') + '</span>';
      });
      thumbs += '</div>';

      /* Each row: a full-width label (checkbox + name, 44px tall) so tapping the name toggles
         it; the size select sits outside the label so choosing a size does not. */
      var rows = '<div class="cd-bd-row"><label class="cd-bd-pick"><input type="checkbox" checked data-bd-anchor>' +
        '<span class="cd-bd-name" title="' + escapeHtml(anchorTitle) + '"><strong>This item</strong> <span class="cd-bd-sz" data-bd-anchor-size></span></span></label>' +
        '<span class="cd-bd-price" data-bd-price="' + anchorPrice + '" data-bd-anchor-price>' + money(anchorPrice) + '</span></div>';
      picked.forEach(function (c, i) {
        var card = c.card;
        var sel = card.querySelector('select[data-cd-up-variant]');
        var hid = card.querySelector('input[data-cd-up-variant]');
        var price = parseFloat(card.dataset.cdUpPrice) || 0;
        var title = escapeHtml(card.dataset.cdUpTitle || '');
        rows += '<div class="cd-bd-row cd-bd-add" data-bd-item data-product="' + card.dataset.cdUpProduct + '" data-kind="' + (card.dataset.kind || '') + '" data-pos="' + (i + 1) + '" data-title="' + title + '" data-base-price="' + price + '">' +
          '<label class="cd-bd-pick"><input type="checkbox" checked><span class="cd-bd-name">' + title + '</span></label>' +
          '<span class="cd-bd-price" data-bd-price="' + price + '">' + money(price) + '</span>' +
          (sel ? '<select class="cd-bd-size" data-bd-variant aria-label="Size for ' + title + '">' + sel.innerHTML + '</select>'
               : '<input type="hidden" data-bd-variant value="' + (hid ? hid.value : '') + '">') +
          '</div>';
      });
      /* Shown only while a hat is checked (the discount needs a jersey and a hat); the discount
         itself is applied at checkout, so the total here is before it. */
      var promo = '';
      if (this.dataset.promo && this.dataset.kind === 'jersey' && picked.some(function (c) { return c.kind === 'hat'; })) {
        promo = '<p class="cd-bd-promo" data-bd-promo>' + escapeHtml(this.dataset.promo) + '</p>';
      }
      wrap.innerHTML = promo + thumbs + rows +
        '<div class="cd-bd-total"><span>Total</span><strong data-bd-total></strong></div>' +
        '<p class="cd-bd-msg" data-bd-msg hidden></p>' +
        '<button type="button" class="cd-bd-go" data-bd-go></button>';
      slot.appendChild(wrap);

      /* The product form always has a size selected; show it and use that variant's price. */
      var variants = [];
      try { variants = JSON.parse((document.querySelector('variant-radios script[type="application/json"], variant-selects script[type="application/json"]') || {}).textContent || '[]'); } catch (e) {}
      /* form.getAttribute('id'): form.id would return the form's <input name="id"> */
      var formId = form && form.getAttribute('id');
      var qtyInput = formId ? document.querySelector('input[name="quantity"][form="' + formId + '"]') : null;
      var qtyOf = () => Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      var syncAnchor = () => {
        var v = variants.find(function (x) { return String(x.id) === String(idInput.value); });
        var q = qtyOf();
        var sz = wrap.querySelector('[data-bd-anchor-size]');
        var pr = wrap.querySelector('[data-bd-anchor-price]');
        if (v) {
          var size = sizeKey(v.option1);
          wrap.querySelectorAll('select[data-bd-variant]:not([data-touched])').forEach(function (s) {
            var hit = size && findSize(s, size);
            if (hit) s.value = hit.value; else commonSize(s);
          });
          sz.textContent = (v.title && v.title !== 'Default Title' ? '(' + v.title + ')' : '') + (q > 1 ? ' × ' + q : '');
          pr.dataset.bdPrice = String(v.price / 100 * q);
          pr.dataset.bdQty = String(q);
          pr.textContent = money(v.price / 100 * q);
        }
      };
      var update = () => {
        syncAnchor();
        wrap.querySelectorAll('[data-bd-item]').forEach(function (row) {
          var s = row.querySelector('select[data-bd-variant]');
          if (!s) return;
          var p = optionPrice(s, parseFloat(row.dataset.basePrice) || 0);
          var pe = row.querySelector('[data-bd-price]');
          pe.dataset.bdPrice = String(p);
          pe.textContent = money(p);
        });
        var promoEl = wrap.querySelector('[data-bd-promo]');
        if (promoEl) {
          /* The discount needs the jersey and a hat in the order. */
          promoEl.hidden = !wrap.querySelector('[data-bd-anchor]').checked ||
            !Array.prototype.some.call(wrap.querySelectorAll('[data-bd-item][data-kind="hat"] input[type="checkbox"]'), function (cb) { return cb.checked; });
        }
        var total = 0, n = 0;
        wrap.querySelectorAll('input[type="checkbox"]').forEach(function (cb) {
          if (!cb.checked) return;
          var row = cb.closest('.cd-bd-row');
          var pe = row.querySelector('[data-bd-price]');
          total += parseFloat(pe.dataset.bdPrice) || 0;
          n += parseInt(pe.dataset.bdQty, 10) || 1;
        });
        wrap.querySelector('[data-bd-total]').textContent = money(total);
        var go = wrap.querySelector('[data-bd-go]');
        if (wrap.hasAttribute('data-bd-done')) return;
        if (this.busy) { go.disabled = true; return; }
        go.textContent = n > 1 ? 'Add ' + n + ' to cart' : (n === 1 ? 'Add to cart' : 'Select an item');
        go.disabled = n === 0;
      };
      wrap.addEventListener('change', function (e) {
        if (e.target.matches('select[data-bd-variant]')) e.target.setAttribute('data-touched', '');
        update();
      });
      /* The theme swaps the id input's value after a size change; re-read it a tick later. Same
         for the quantity stepper (its +/- buttons set the value without a change event). */
      document.addEventListener('change', (e) => {
        if (e.target.closest && e.target.closest('variant-radios, variant-selects')) setTimeout(update, 50);
      });
      if (qtyInput) {
        qtyInput.addEventListener('input', update);
        qtyInput.addEventListener('change', update);
        var stepper = qtyInput.closest('quantity-input') || qtyInput.parentElement;
        if (stepper) stepper.addEventListener('click', () => setTimeout(update, 50));
      }
      wrap.querySelector('[data-bd-go]').addEventListener('click', () => this.addBundle(wrap, idInput, form, update));
      update();
      this.bundle = true;
      return true;
    }

    addBundle(wrap, idInput, form, update) {
      if (this.busy) return;
      var msg = wrap.querySelector('[data-bd-msg]');
      msg.hidden = true;
      var anchorBox = wrap.querySelector('[data-bd-anchor]');
      if (anchorBox.checked && !idInput.value) {
        msg.textContent = 'Choose a size above first.';
        msg.hidden = false;
        var picker = document.querySelector('variant-radios, variant-selects');
        if (picker) picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      var added = [];
      var addons = [];
      wrap.querySelectorAll('[data-bd-item]').forEach(function (row) {
        if (!row.querySelector('input[type="checkbox"]').checked) return;
        var v = row.querySelector('[data-bd-variant]');
        if (!v || !v.value) return;
        addons.push({ id: Number(v.value), quantity: 1, properties: { _upsell: 'product-page-bundle' } });
        added.push(row);
      });
      var anchorPriceEl = wrap.querySelector('[data-bd-anchor-price]');
      var qty = parseInt(anchorPriceEl && anchorPriceEl.dataset.bdQty, 10) || 1;  /* what the row shows */
      var go = wrap.querySelector('[data-bd-go]');
      /* The main Add to Cart stays off while this runs, so the two can't both add the product. */
      var mainAdd = form && form.querySelector('[type="submit"][name="add"]');
      var mainWas = mainAdd ? mainAdd.disabled : false;
      var release = () => { if (mainAdd) mainAdd.disabled = mainWas; };
      if (mainAdd) mainAdd.disabled = true;
      this.busy = true;
      go.disabled = true;
      var sections = (this.dataset.sections || '').split(',').filter(Boolean);

      var skippedAnchor = false;
      /* If the shopper already added this size with the main button, add only the extras. */
      fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .catch(function () { return { items: [] }; })
        .then((cart) => {
          var items = addons.slice();
          var inCart = (cart.items || []).some(function (i) { return String(i.variant_id) === String(idInput.value); });
          if (anchorBox.checked && inCart) {
            /* Already added with the main button: add only the extras (said once they're in). */
            anchorBox.checked = false;
            skippedAnchor = true;
            update();
          } else if (anchorBox.checked) {
            items.unshift({ id: Number(idInput.value), quantity: qty });
          }
          if (!items.length) {
            this.busy = false; release(); update();
            if (skippedAnchor) { msg.textContent = 'This item is already in your cart.'; msg.hidden = false; }
            return null;
          }
          var body = { items: items };
          if (sections.length) { body.sections = sections.join(','); body.sections_url = window.location.pathname; }
          return fetch(window.routes.cart_add_url + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          }).then((r) => r.json());
        })
        .then((state) => {
          if (!state) return;
          if (state.status) {
            this.busy = false; release(); update();
            msg.textContent = state.description || 'One of these just sold out. Please adjust and try again.';
            msg.hidden = false;
            return;
          }
          /* Done: keep the button off so a second tap cannot add the whole set again. */
          wrap.setAttribute('data-bd-done', '');
          go.textContent = 'Added to cart';
          go.disabled = true;
          if (skippedAnchor) { msg.textContent = 'This item is already in your cart, so we added the extras.'; msg.hidden = false; }
          /* Open the drawer right away (no reload here, so analytics can finish in the background),
             then let the main Add to Cart work again. */
          this.busy = false;
          this.dispatchEvent(new CustomEvent('cart-upsell:added', { bubbles: true, detail: state }));
          release();
          added.forEach((row) => track('Upsell Added', 'pjs_upsell_added', {
            placement: 'product-page-bundle',
            team: this.dataset.team,
            product: row.dataset.title || '',
            price: row.querySelector('[data-bd-price]').dataset.bdPrice || '',
            kind: row.dataset.kind || '',
            position: row.dataset.pos || ''
          }));
          if (added.length) setUpsellAttribute('product-page-bundle');
        })
        .catch(() => { this.busy = false; release(); update(); msg.textContent = 'Something went wrong. Please try again.'; msg.hidden = false; });
    }

    /* Count a view only when the block is on screen, once per placement and team. */
    watchView() {
      var placement = this.bundle ? 'product-page-bundle' : this.dataset.placement;
      var key = placement + '|' + this.dataset.team;
      if (viewed[key]) return;
      var report = () => {
        if (viewed[key]) return;
        viewed[key] = true;
        track('Upsell Viewed', 'pjs_upsell_viewed', {
          placement: placement,
          team: this.dataset.team,
          anchor: this.dataset.kind + '/' + this.dataset.audience,
          shown: String(this.shown),
          kinds: this.shownKinds || ''
        });
      };
      if (!('IntersectionObserver' in window)) { report(); return; }
      this.seen = new IntersectionObserver((entries) => {
        if (entries.some(function (en) { return en.isIntersecting; })) { this.seen.disconnect(); report(); }
      }, { threshold: 0.5 });
      this.seen.observe(this);
    }

    add(button) {
      if (this.busy) return;
      var card = button.closest('[data-cd-up-product]');
      var field = card && card.querySelector('[data-cd-up-variant]');
      if (!field || !field.value) return;
      var placement = this.dataset.placement || 'cart-drawer';
      var team = this.dataset.team;
      var sections = (this.dataset.sections || '').split(',').filter(Boolean);
      var body = { items: [{ id: Number(field.value), quantity: 1, properties: { _upsell: placement } }] };
      if (sections.length) { body.sections = sections.join(','); body.sections_url = window.location.pathname; }
      this.busy = true;
      button.disabled = true;

      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then((r) => r.json())
        .then((state) => {
          if (state.status) { this.busy = false; button.disabled = false; button.textContent = 'Unavailable'; return; }
          /* Wait for the order marker and the event before anything re-renders or reloads. */
          return Promise.all([
            setUpsellAttribute(placement),
            track('Upsell Added', 'pjs_upsell_added', {
              placement: placement,
              team: team,
              product: card.dataset.cdUpTitle || '',
              price: card.dataset.cdUpPrice || '',
              kind: card.dataset.kind || '',
              position: card.dataset.pos || ''
            })
          ]).then(() => {
            this.busy = false;
            if (this.isConnected) {
              this.dispatchEvent(new CustomEvent('cart-upsell:added', { bubbles: true, detail: state }));
              return;
            }
            /* The drawer re-rendered while the request was in flight: update it directly. */
            var drawer = document.querySelector('cart-drawer');
            if (drawer && state.sections && state.sections['cart-drawer'] && drawer.renderContents) drawer.renderContents(state);
            else if (document.querySelector('cart-items')) window.location.reload();
          });
        })
        .catch(() => { this.busy = false; button.disabled = false; });
    }
  });

  /* Rendered by the drawer and cart page when the cart still carries _upsell_used but no line
     has the _upsell property any more (the add-on was removed). Clears the attribute so the
     order is not attributed to "Complete the look". */
  if (!customElements.get('cart-upsell-cleanup')) {
    customElements.define('cart-upsell-cleanup', class extends HTMLElement {
      connectedCallback() { clearUpsellAttributeIfStale(); }
    });
  }
})();
